import "core-js/modules/es.string.replace";
import "core-js/modules/web.dom-collections.iterator";
import "core-js/modules/es.string.replace";
import "core-js/modules/web.dom-collections.iterator";
/**
 * Component: Product form
  * ------------------------------------------------------------------------------
 * Highly coupled scripts to control the behaviour of a product form.
 *
 * @namespace productForm
 */

import { formatMoney } from '@shopify/theme-currency';
import cssClasses from '~/helpers/cssClasses';
import { on } from '~/helpers/utils';
import bind from '~/mixins/bind';
import ProductSwatches from '~/components/product-swatches';
/**
 * DOM selectors.
 */

const selectors = {
  addToCart: '[js-product-form="addToCart"]',
  formFooter: '[js-product-form="formFooter"]',
  comparePrice: '[js-product-form="comparePrice"]',
  compareAtPriceInput: '[js-product-form="compareAtPriceInput"]',
  container: '[js-product-form="container"]',
  masterSelector: '[js-product-form="masterSelector"]',
  maxQuantityInput: '[js-product-form="maxQuantityInput"]',
  notification: '[js-product-form="notification"]',
  optionSelector: '[js-product-form="optionSelector"]',
  price: '[js-product-form="price"]',
  priceWrapper: '[js-product-form="priceWrapper"]',
  productJson: '[js-product-json]',
  cookieBanner: '[js-cookie-banner="container"]',
  preorder: '[js-product-form="preorder"]'
};
const ctaIcon = "\n  <svg class=\"icon icon__forward-arrow-cta\" width=\"19\" height=\"14\" viewBox=\"0 0 19 14\" xmlns=\"http://www.w3.org/2000/svg\">\n    <path d=\"M18.601 7.60104C18.933 7.2691 18.933 6.73091 18.601 6.39896L13.1917 0.989593C12.8597 0.657648 12.3215 0.657648 11.9896 0.989593C11.6576 1.32154 11.6576 1.85973 11.9896 2.19167L16.7979 7L11.9896 11.8083C11.6576 12.1403 11.6576 12.6785 11.9896 13.0104C12.3215 13.3424 12.8597 13.3424 13.1917 13.0104L18.601 7.60104ZM-7.43094e-08 7.85L18 7.85L18 6.15L7.43094e-08 6.15L-7.43094e-08 7.85Z\"/>\n  </svg>";
/**
 * Create a new product form object.
 * @param {Object} config - Configuration.
 */

export default (config => {
  /**
   * Instance settings.
   */
  const settings = {
    enableHistoryState: config.enableHistoryState || false
  };
  /**
   * DOM node selectors.
   */

  const container = document.querySelector("[js-product-form=\"".concat(config.container, "\"]"));
  const nodeSelectors = {
    addToCart: container.querySelector(selectors.addToCart),
    formFooter: container.querySelector(selectors.formFooter),
    comparePrice: container.querySelector(selectors.comparePrice),
    masterSelector: container.querySelector(selectors.masterSelector),
    optionSelector: container.querySelectorAll(selectors.optionSelector),
    price: container.querySelector(selectors.price),
    priceWrapper: container.querySelector(selectors.priceWrapper),
    maxQuantityInput: container.querySelector(selectors.maxQuantityInput),
    productJson: container.querySelector(selectors.productJson),
    cookieBanner: document.querySelector(selectors.cookieBanner),
    preorder: container.querySelectorAll(selectors.preorder)
  };
  /**
   * Initialise component bind.
   */

  const binder = bind(container);
  /**
   * Global instances.
   */

  const productData = Object.freeze(JSON.parse(nodeSelectors.productJson.innerHTML));
  const variantData = productData.variants;
  let currentVariant = {};
  /**
   * Initialise component.
   */

  function init() {
    if (binder.isSet()) {
      return;
    }

    if (currentVariant) {
      currentVariant = getVariantFromOptions();
    }

    setDefaultState();
    setEventListeners();
    setEventBusListeners();
    binder.set();
  }
  /**
   * Set default state.
   */


  function setDefaultState() {
    if (theme.features.productSwatches) {
      ProductSwatches({
        data: productData
      }).init();
    }

    updateFormState();
  }
  /**
   * Update notification state.
   * @param {Object} variant - Current variant object.
   */


  function updateNotificationState(variant) {
    if (isNotificationCreated()) {
      container.querySelector(selectors.notification).remove();
    }

    if (variant) {
      const quantity = getVariantInventoryQuantity(variant);

      if (!quantity) {
        return;
      }

      if (quantity <= theme.features.inventoryThreshold && quantity > 0) {
        container.appendChild(createNotification(quantity));
      }
    }
  }
  /**
   * Check if notification exists.
   * @returns {Boolean}
   */


  function isNotificationCreated() {
    return Boolean(container.querySelector(selectors.notification));
  }
  /**
   * Create notification node.
   * @param {Number} quantity - Current variant inventory quantity.
   * @returns {HTMLElement}
   */


  function createNotification(quantity) {
    const message = getLowStockMessage(quantity);
    const notification = document.createElement('div');
    notification.classList.add('notification');
    notification.innerHTML = getNotificationTemplate(message);
    notification.setAttribute('js-product-form', 'notification');
    return notification;
  }
  /**
   * Get notification template.
   * @param {String} message - text to render inside notification.
   * @returns {String}
   */


  function getNotificationTemplate(message) {
    return "\n      <div class=\"notification__text-wrapper\">\n        <p class=\"notification__text\">\n          ".concat(message, "\n        </p>\n      </div>\n    ");
  }
  /**
   * Get low stock notification message correctly pluralised.
   * @param {Number} quantity - Quantity of line item.
   * @returns {String}
   */


  function getLowStockMessage(quantity) {
    if (quantity === 1) {
      return theme.low_stock_message.single;
    }

    return theme.low_stock_message.multiple.replace('%quantity%', quantity);
  }
  /**
   * Get variant inventory quantity from product-json.liquid variant data.
   * @param {Object || undefined} - Variant object.
   */


  function getVariantInventoryQuantity(variant) {
    if (!variantData.length) {
      return 0;
    }

    return Object.keys(variantData).map(key => {
      if (variantData[key].id === variant.id) {
        return variantData[key].inventory_quantity;
      }

      return null;
    }).filter(Boolean)[0];
  }
  /**
   * Set EventBus listeners.
   */


  function setEventBusListeners() {
    Frame.EventBus.listen('Variant:priceChange', response => onPriceChange(response));
    Frame.EventBus.listen('ProductSwatches:available', response => handleSwatchAvailability(response));
    Frame.EventBus.listen('ProductSwatches:optionSelected', response => handleOptionChange(response));
    Frame.EventBus.listen('ProductSwatches:reset', () => resetForm());
    Frame.EventBus.listen('ProductSwatches:soldOut', () => updateAddToCartState(theme.strings.soldOut));
  }
  /**
   * Set event listeners on select elements.
   */


  function setEventListeners() {
    [...nodeSelectors.optionSelector].forEach(element => {
      if (element.nodeName === 'SELECT') {
        on('change', element, () => handleSelectChange());
      }
    });

    window.onload = function () {
      var _nodeSelectors$cookie, _nodeSelectors$formFo;

      const desktopOffset = ((_nodeSelectors$cookie = nodeSelectors.cookieBanner) === null || _nodeSelectors$cookie === void 0 ? void 0 : _nodeSelectors$cookie.offsetHeight) || 0;
      const mobileOffset = ((_nodeSelectors$formFo = nodeSelectors.formFooter) === null || _nodeSelectors$formFo === void 0 ? void 0 : _nodeSelectors$formFo.offsetHeight) || 0;

      if (window.zE) {
        window.zE('webWidget', 'updateSettings', {
          webWidget: {
            offset: {
              vertical: "".concat(desktopOffset, "px"),
              mobile: {
                vertical: "".concat(mobileOffset, "px")
              }
            },
            zIndex: 23
          }
        });
      } else {
        window.zESettings = {
          webWidget: {
            offset: {
              vertical: "".concat(desktopOffset, "px"),
              mobile: {
                vertical: "".concat(mobileOffset, "px")
              }
            },
            zIndex: 23
          }
        };
      }
    };
  }
  /**
   * Get currently selected options from product form.
   * - Works with all form elements with an option selector selector.
   * @returns {Array}
   */


  function getCurrentOptions() {
    const currentOptions = [];
    [...nodeSelectors.optionSelector].forEach(element => {
      const type = element.getAttribute('type');
      const currentOption = {};

      if (type === 'radio' || type === 'checkbox') {
        if (element.checked) {
          currentOption.value = element.value;
          currentOption.index = element.dataset.index;
          currentOptions.push(currentOption);
        }
      } else {
        currentOption.value = element.value;
        currentOption.index = element.dataset.index;
        currentOptions.push(currentOption);
      }
    });
    return currentOptions;
  }
  /**
   * Find variant from product.variants based on selected values.
   * @returns {Object|undefined}
   */


  function getVariantFromOptions() {
    const selectedValues = getCurrentOptions();
    const variants = productData.variants;
    let found = false;
    variants.forEach(variant => {
      let satisfied = true;
      selectedValues.forEach(option => {
        if (satisfied) {
          satisfied = option.value === variant[option.index];
        }
      });

      if (satisfied) {
        found = variant;
      }
    });
    return found || null;
  }
  /**
   * Event handler for when a variant price changes.
   * @param {Object} response - Selected variant data.
   */


  function onPriceChange(response) {
    const form = document.querySelector("[js-product-form=\"".concat(config.container, "\"]"));
    const priceSelector = form.querySelector(selectors.price);
    priceSelector.setAttribute('data-price', response.price);
    priceSelector.innerText = formatMoney(response.price, theme.moneyFormat);
    const compareAtPriceInputSelector = form.querySelector(selectors.compareAtPriceInput);
    compareAtPriceInputSelector.setAttribute('value', response.compare_at_price);

    if (form.querySelector(selectors.comparePrice)) {
      const comparePriceSelector = form.querySelector(selectors.comparePrice);
      comparePriceSelector.setAttribute('data-price', response.compare_at_price);

      if (response.compare_at_price > 0) {
        comparePriceSelector.innerText = formatMoney(response.compare_at_price, theme.moneyFormat);
        comparePriceSelector.classList.remove(cssClasses.hidden);
      } else {
        comparePriceSelector.innerText = '';
        comparePriceSelector.classList.add(cssClasses.hidden);
      }
    }

    if (theme.features.currencyConverter) {
      Frame.EventBus.emit('Currency:convertAll');
    }
  }
  /**
   * Handle default availability.
   */


  function handleSwatchAvailability(response) {
    if (!response.formCompleted) {
      disableAddToCart(theme.strings.prompt);
    }
  }
  /**
   * Event handler for when an input changes on the form.
   * @param {Object} response - Pay load from option selected.
   */


  function handleOptionChange(response) {
    /**
     * 'Change' event dispatched on swatch click.
     * Klaviyo adds a 'change' event listener on the body
     * which is used to get the variant id.
     */
    const event = new Event('change');

    if (settings.enableHistoryState && currentVariant) {
      updateHistoryState(currentVariant);
    }

    Frame.EventBus.emit('ProductForm:change', {
      currentOptions: getCurrentOptions(),
      currentVariant: response.formCompleted ? getVariantFromOptions() : null,
      currentInventory: getVariantInventoryQuantity(getVariantFromOptions())
    });

    if (!response.formCompleted) {
      return;
    }

    updateFormState();
    document.body.dispatchEvent(event);
  }
  /**
   * Event handler for when a select element changes on the store.
   */


  function handleSelectChange() {
    updateFormState();
  }
  /**
   * Update form state based on current selections.
   */


  function updateFormState() {
    const variant = getVariantFromOptions();
    updateNotificationState(variant);
    updateAddToCartState(variant);
    updateMaxQuantityState(variant);

    if (!variant) {
      return;
    }

    updateMasterSelect(variant);
    updatePrice(variant);
    currentVariant = variant;

    if (settings.enableHistoryState) {
      updateHistoryState(variant);
    }
  }
  /**
   * Updates the DOM state of the add to cart button.
   * @param {{Object || undefined}} variant - Currently selected variant.
   */


  function updateAddToCartState(variant) {
    if (variant.available) {
      enableAddToCart();
      Frame.EventBus.emit('Variant:isAvailable', variant);
    } else {
      disableAddToCart();
    }

    const preorder = container.querySelector(selectors.preorder);

    if (!preorder) {
      return;
    }

    if (preorder.value && variant.inventory_policy === 'continue' && variant.inventory_quantity <= 0) {
      enableAddToCart();
      nodeSelectors.addToCart.innerHTML = "".concat(theme.strings.preorder_cta_label).concat(ctaIcon);
    }
  }
  /**
   * Update max quantity value of quantity selectors.
   * @param {Object} variant - Currently selected variant.
   */


  function updateMaxQuantityState(variant) {
    const quantity = getVariantInventoryQuantity(variant) > 0 ? getVariantInventoryQuantity(variant) : 999;
    nodeSelectors.maxQuantityInput.value = quantity;
  }
  /**
   * Trigger event when variant price changes.
   * @param {Object} variant - Currently selected variant.
   */


  function updatePrice(variant) {
    Frame.EventBus.emit('Variant:priceChange', variant);
  }
  /**
   * Update hidden master select of variant change.
   * @param {Object} variant - Currently selected variant.
   */


  function updateMasterSelect(variant) {
    nodeSelectors.masterSelector.value = variant.id;
  }
  /**
   * Update history state for product deep linking.
   * @param {Object} variant - Currently selected variant.
   */


  function updateHistoryState(variant) {
    if (!window.history.replaceState || !variant) {
      return;
    }

    const newurl = "\n      ".concat(window.location.protocol, "//").concat(window.location.host).concat(window.location.pathname, "?variant=").concat(variant.id, "\n    ");
    window.history.replaceState({
      path: newurl
    }, '', newurl);
  }
  /**
   * Reset form and assign prompt text to add to cart button.
   */


  function resetForm() {
    disableAddToCart();

    if (productData.available) {
      nodeSelectors.addToCart.innerHTML = "".concat(theme.strings.addToCart).concat(ctaIcon);
    }
  }
  /**
   * Add disable state to add to cart button.
   * @param {String} label - Text to use on button.
   */


  function disableAddToCart() {
    let label = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : theme.strings.soldOut;
    nodeSelectors.addToCart.setAttribute('disabled', true);
    nodeSelectors.addToCart.innerText = label;
  }
  /**
   * Set add to cart button to active.
   */


  function enableAddToCart() {
    nodeSelectors.addToCart.removeAttribute('disabled');
    nodeSelectors.addToCart.innerHTML = "".concat(theme.strings.addToCart).concat(ctaIcon);
  }
  /**
   * Expose public interface.
   */


  return Object.freeze({
    init,
    getVariantFromOptions
  });
});